import { createTheme } from '@mui/material/styles';

/**
 * Partners Portal Theme: the elebyte.io look, shared with Support, Education, Control and Auth
 * (Support/src/theme/index.js) so every Elebyte surface reads as one product.
 * Black stage, cyan and white type, brass as the only warm colour (warnings and errors too).
 * Display type is Inter 900 italic tracked tight; actions are Inter 900 italic uppercase; the
 * system's own words (numbers, dates, statuses) are Departure Mono. Component classes that aren't
 * MUI live in src/index.css.
 */
const CYAN = '#66E0FA';
const CYAN_DEEP = '#3EC2E9';
const BRASS = '#B8853A';
const NAVY = '#0a0e1a';
const NAVY_PANEL = '#121829';
const INK = '#04222b';
const RULE = 'rgba(255, 255, 255, 0.12)';
const SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
const SANS = "'Inter', 'Helvetica Neue', Arial, sans-serif";
const MONO = "'Departure Mono', ui-monospace, 'SF Mono', Menlo, monospace";

const display = { fontWeight: 900, fontStyle: 'italic', letterSpacing: '-0.035em', lineHeight: 1.08 };
const action = { fontWeight: 900, fontStyle: 'italic', letterSpacing: '0.06em', textTransform: 'uppercase' };
const eyebrow = { fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11 };

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: CYAN, light: '#8FEAFC', dark: CYAN_DEEP, contrastText: '#000000' },
    secondary: { main: '#FFFFFF', contrastText: '#000000' },
    background: { default: '#000000', paper: NAVY_PANEL },
    text: { primary: '#FFFFFF', secondary: 'rgba(255, 255, 255, 0.66)', disabled: 'rgba(255, 255, 255, 0.38)' },
    divider: RULE,
    error: { main: BRASS, contrastText: '#000000' },
    warning: { main: BRASS, contrastText: '#000000' },
    success: { main: CYAN, contrastText: '#000000' },
    info: { main: CYAN, contrastText: '#000000' },
    action: { hover: 'rgba(102, 224, 250, 0.08)', selected: 'rgba(102, 224, 250, 0.12)' },
  },
  typography: {
    fontFamily: SANS,
    h1: display,
    h2: display,
    h3: display,
    h4: display,
    h5: { ...display, letterSpacing: '-0.025em' },
    h6: { fontWeight: 800, fontStyle: 'italic', letterSpacing: '-0.015em' },
    subtitle2: eyebrow,
    button: action,
    caption: { fontFamily: MONO, fontSize: 11, letterSpacing: '0.02em' },
    overline: eyebrow,
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: { body: { backgroundColor: '#000000' } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: {
          ...action,
          fontSize: 12,
          minHeight: 42,
          padding: '0 22px',
          borderRadius: 12,
          transition: `transform 0.25s ${SPRING}, background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease`,
          '&:hover': { transform: 'scale(1.04)' },
          '&.Mui-disabled': { transform: 'none' },
        },
        containedPrimary: { '&:hover': { backgroundColor: '#8FEAFC' } },
        outlined: { borderColor: 'rgba(255, 255, 255, 0.22)', color: '#FFFFFF', '&:hover': { borderColor: CYAN, color: CYAN, backgroundColor: 'transparent' } },
        text: { padding: '0 8px', '&:hover': { backgroundColor: 'transparent', color: '#FFFFFF' } },
        sizeSmall: { minHeight: 34, fontSize: 11 },
        sizeLarge: { minHeight: 50, fontSize: 13 },
      },
    },
    MuiIconButton: { defaultProps: { disableRipple: true } },
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
      // Ripples are off, and MUI's ButtonBase sets `outline: 0` (which beats a global
      // :focus-visible rule, since MUI injects its styles later). Without this, keyboard focus is
      // invisible on every button, icon button, checkbox and switch. The ring sits outside the
      // control, so it reads against the black stage even on a cyan filled button.
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': { outline: `2px solid ${CYAN}`, outlineOffset: 2 },
        },
      },
    },
    MuiCard: {
      styleOverrides: { root: { backgroundImage: 'none', backgroundColor: 'transparent', border: 0, borderTop: `1px solid ${RULE}`, borderRadius: 0 } },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontFamily: MONO, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', borderRadius: 6, height: 28 },
        outlined: { borderColor: 'rgba(255, 255, 255, 0.2)' },
        deleteIcon: { color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: BRASS } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: 'rgba(255, 255, 255, 0.025)',
          fontSize: 16,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.16)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.4)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: CYAN, borderWidth: 1.5 },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: BRASS },
        },
        input: { '&::placeholder': { color: 'rgba(255, 255, 255, 0.6)', opacity: 0.64 } },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.62)',
          '&.Mui-focused': { color: CYAN },
          '&.Mui-error': { color: BRASS },
          // The floating label sits on the box's top border: give it the page's black behind it.
          '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
            ...eyebrow, fontSize: 13, paddingLeft: 4, paddingRight: 6, backgroundColor: '#000000', borderRadius: 3, zIndex: 1,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { marginLeft: 2, fontSize: 12, color: 'rgba(255, 255, 255, 0.52)', '&.Mui-error': { color: BRASS } } },
    },
    MuiSelect: {
      styleOverrides: { icon: { color: CYAN } },
    },
    MuiMenu: {
      styleOverrides: { paper: { backgroundColor: NAVY_PANEL, border: `1px solid ${RULE}`, borderRadius: 12, marginTop: 6 } },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14, minHeight: 42,
          '&:hover': { backgroundColor: 'rgba(102, 224, 250, 0.08)', color: CYAN },
          '&.Mui-selected': { backgroundColor: 'rgba(102, 224, 250, 0.12)', color: CYAN, fontWeight: 700 },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(102, 224, 250, 0.16)' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { marginBottom: 16, minHeight: 44, borderBottom: `1px solid ${RULE}`, '&.MuiTabs-vertical': { marginBottom: 0 } },
        indicator: { height: 3, backgroundColor: CYAN, transform: 'skewX(-12deg)', borderRadius: 1 },
        scrollButtons: { color: CYAN },
      },
    },
    MuiTab: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          ...action, fontSize: 12, minHeight: 44, minWidth: 0, padding: '0 2px', marginRight: 26,
          color: 'rgba(255, 255, 255, 0.55)', transition: 'color 0.2s ease',
          '&:hover': { color: '#FFFFFF' },
          '&.Mui-selected': { color: '#FFFFFF' },
        },
      },
    },
    MuiAlert: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        root: { borderRadius: 0, borderLeft: `2px solid ${CYAN}`, backgroundColor: 'rgba(255, 255, 255, 0.03)', color: 'rgba(255, 255, 255, 0.86)', fontSize: 14, alignItems: 'center' },
        standardError: { borderLeftColor: BRASS, '& .MuiAlert-icon': { color: BRASS } },
        standardWarning: { borderLeftColor: BRASS, '& .MuiAlert-icon': { color: BRASS } },
        standardSuccess: { '& .MuiAlert-icon': { color: CYAN } },
        standardInfo: { '& .MuiAlert-icon': { color: CYAN } },
        filled: { borderRadius: 12, borderLeft: 0, fontWeight: 600 },
        filledSuccess: { backgroundColor: CYAN, color: '#000000' },
        filledInfo: { backgroundColor: CYAN, color: '#000000' },
        filledError: { backgroundColor: BRASS, color: '#000000' },
        filledWarning: { backgroundColor: BRASS, color: '#000000' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: NAVY, border: `1px solid ${RULE}`, borderRadius: 16, boxShadow: '0 30px 80px rgba(0, 0, 0, 0.6)' },
      },
    },
    MuiBackdrop: {
      styleOverrides: { root: { backgroundColor: 'rgba(0, 0, 0, 0.72)', backdropFilter: 'blur(6px)' } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { ...display, fontSize: 28, padding: '28px 28px 8px' } },
    },
    MuiDialogContent: { styleOverrides: { root: { padding: '8px 28px' } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '16px 28px 24px', gap: 8 } } },
    MuiDialogContentText: { styleOverrides: { root: { color: 'rgba(255, 255, 255, 0.72)', fontSize: 15, lineHeight: 1.65 } } },
    MuiPagination: {
      styleOverrides: { ul: { justifyContent: 'center', gap: 4 } },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          fontFamily: MONO, fontSize: 12, borderRadius: 4, color: 'rgba(255, 255, 255, 0.66)',
          '&:hover': { backgroundColor: 'rgba(102, 224, 250, 0.08)', color: CYAN },
          // The chosen page sits on a skewed cyan strip; the number itself stays level (text never tilts).
          isolation: 'isolate',
          '&.Mui-selected': {
            backgroundColor: 'transparent', color: INK,
            '&::before': { content: '""', position: 'absolute', inset: 0, zIndex: -1, borderRadius: 4, backgroundColor: CYAN, transform: 'skewX(-12deg)' },
            '&:hover': { backgroundColor: 'transparent' },
          },
        },
      },
    },
    MuiRating: {
      styleOverrides: { iconFilled: { color: CYAN }, iconHover: { color: CYAN }, iconEmpty: { color: 'rgba(255, 255, 255, 0.24)' } },
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: { root: { backgroundColor: 'rgba(255, 255, 255, 0.06)', borderRadius: 4 }, text: { transform: 'scale(1, 0.72)' } },
    },
    MuiCircularProgress: { defaultProps: { thickness: 5 } },
    MuiSnackbar: { defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' } } },
    MuiTooltip: {
      styleOverrides: { tooltip: { backgroundColor: CYAN, color: '#000000', ...eyebrow, fontStyle: 'italic', fontWeight: 900, borderRadius: 8, padding: '6px 10px' } },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: RULE } } },
  },
});

export default theme;
